/**
 * PermissionsContext — the resolved Roles & Access grid for the signed-in
 * user, fetched once per session from GET /permission-sets/me.
 *
 * WHY THE UI READS THIS AT ALL
 * ────────────────────────────
 * The backend already enforces the matrix. The frontend reads the same grid
 * for one reason: a page that loads and *then* 403s renders as an empty
 * screen, and an empty screen reads as "the app is broken", not "you don't
 * have access". Hiding the nav entry is the honest outcome. (Same rule as
 * the permission-flow check in CLAUDE.md.)
 *
 * FAIL-OPEN, DELIBERATELY
 * ───────────────────────
 * `can()` returns true while loading and true on any fetch failure. The
 * server is the authority — if this call fails we must not blank out the
 * sidebar of a user who is perfectly authorised. The worst case of failing
 * open here is a visible 403 toast; the worst case of failing closed is a
 * consultant staring at an empty app during their first login.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { permissionSetAPI } from '../utils/api';
import { useAuth } from './AuthContext';

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    loading: true,
    permissions: null,     // null = "not resolved yet" → can() says yes
    roleLabel: null,
    baseRole: null,
  });

  const refresh = useCallback(async () => {
    if (!user) {
      setState({ loading: false, permissions: null, roleLabel: null, baseRole: null });
      return;
    }
    try {
      const { data } = await permissionSetAPI.me();
      setState({
        loading: false,
        permissions: data?.permissions || null,
        roleLabel: data?.role_label || null,
        baseRole: data?.base_role || null,
      });
    } catch {
      // See the fail-open note above.
      setState({ loading: false, permissions: null, roleLabel: null, baseRole: null });
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  /**
   * can('leads.delete') → boolean.
   * Unknown capability, unresolved grid, or a capability the grid doesn't
   * mention all resolve to TRUE — only an explicit false denies, mirroring
   * the backend's "the matrix subtracts, never adds" rule exactly.
   */
  const can = useCallback((capability) => {
    if (!capability) return true;
    const perms = state.permissions;
    if (!perms) return true;
    return perms[capability] !== false;
  }, [state.permissions]);

  return (
    <PermissionsContext.Provider value={{ ...state, can, refresh }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext) || {
    loading: false, permissions: null, roleLabel: null, baseRole: null,
    can: () => true, refresh: () => {},
  };
}

/** Shorthand for the common case. */
export function useCan() {
  return usePermissions().can;
}

export default PermissionsContext;
